import { Question } from '../../../../types/api';
export const toSentenceCase = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
/**
 * Helper function to create AnswerValue structure
 */
export const createAnswerValue = (
  selectedOptionId: string | null = null,
  typedValue: string | null = null
) => ({
  selectedOptionId,
  typedValue,
});

/**
 * Determine text input visibility based on validation rules
 */
export const shouldShowTextInput = (
  questionType: string,
  radioValue: string | null,
  question: Question
): boolean => {
  if (questionType === 'radio/text') {
    return true; // Always show text input for radio/text type
  }
  // For radio-text type, check validation rules
  if (question.validation_rules?.show_on_options) {
    if (radioValue) {
      const showOnOptions =
        question.validation_rules.show_on_options.map(String);
      return showOnOptions.includes(String(radioValue));
    }
    return false; // Don't show if no option selected and rules exist
  }

  return true; // Show by default if no rules defined
};

/**
 * Get input type based on validation rules
 */
export const getInputType = (question: Question): string => {
  const dataType = question.validation_rules?.data_type;
  return dataType === 'number' ? 'number' : dataType === 'url' ? 'url' : 'text';
};

/**
 * Check if upload section should be shown for radio-based questions
 */
export const shouldShowUploadSection = (
  question: Question,
  value: any
): boolean => {
  if (
    question.type === 'radio' ||
    question.type === 'radio-text' ||
    question.type === 'radio/text'
  ) {
    const selectedOptionId = value?.selectedOptionId;
    const typedValue = value?.typedValue;

    let optionHasAttachment = false;
    let textInputProvided = false;

    if (selectedOptionId) {
      const selectedOption = question.options?.find(
        option => String(option.id) === String(selectedOptionId)
      );
      optionHasAttachment =
        selectedOption?.has_attachment === true && !!question.hasAttachment;
    }

    if (
      typedValue !== undefined &&
      typedValue !== null &&
      typedValue.trim().length > 0
    ) {
      textInputProvided =
        typedValue?.trim().length > 0 && !!question.hasAttachment;
    }

    return optionHasAttachment || textInputProvided;
  } else if (
    question.type === 'text' ||
    question.type === 'number' ||
    question.type === 'url' ||
    question.type === 'textarea' ||
    question.type === 'email'
  ) {
    const typedValue = value?.typedValue;
    return typedValue?.trim().length > 0 && !!question.hasAttachment;
  }
  // For other field types, use the original logic
  return !!question.hasAttachment;
};

/**
 * Check if upload section should be shown for RadioGrid component
 * Special logic: Once any subquestion selects an option with attachment,
 * keep showing upload section until answers change
 */
export const shouldShowUploadSectionForRadioGrid = (
  question: Question,
  answers?: Record<string, any>
): boolean => {

  // If question doesn't have attachment capability, don't show upload section
  if (!question.hasAttachment) {
    console.log('RadioGrid: No attachment capability on question');
    return false;
  }

  // If no subquestions, don't show
  if (!question.subQuestions || question.subQuestions.length === 0) {
    console.log('RadioGrid: No subquestions');
    return false;
  }

  console.log('RadioGrid: Checking subquestions for attachment options', {
    questionId: question.id,
    subQuestionsCount: question.subQuestions.length,
    answers
  });

  // Check if ANY subquestion has selected an option with has_attachment: true
  for (const subQuestion of question.subQuestions) {
    const answer = answers?.[subQuestion.id];
    
    if (!answer?.value) continue;

    // Extract selectedOptionId from AnswerValue structure
    const selectedOptionId = answer.value?.selectedOptionId || answer.value;
    
    if (selectedOptionId) {
      // Find the selected option in the subquestion's options
      const selectedOption = subQuestion.options?.find(
        option => String(option.id) === String(selectedOptionId)
      );
      
      console.log('RadioGrid: Checking subquestion', {
        subQuestionId: subQuestion.id,
        selectedOptionId,
        selectedOption,
        has_attachment: selectedOption?.has_attachment
      });
      
      // If this option has attachment enabled, show upload section
      if (selectedOption?.has_attachment === true) {
        console.log('RadioGrid: Upload section should be shown - option has attachment');
        return true;
      }
    }
  }

  console.log('RadioGrid: Upload section should NOT be shown - no attachment options selected');
  return false;
};

/**
 * Get placeholder text for input fields
 */
export const getPlaceholder = (question: Question): string => {
  return toSentenceCase(
    question.validation_rules?.placeholder ??
    question.validation_rules?.unit ??
    question.placeholder ??
    'Enter text'
  );
};

/**
 * Get common input props based on question configuration
 */
export const getInputProps = (question: Question) => ({
  required: question.validation_rules?.required_when_visible ?? false,
  minLength: question.validation_rules?.min_length,
  maxLength: question.validation_rules?.max_length,
  min:
    question.validation_rules?.data_type === 'number'
      ? question.validation_rules?.min_value
      : undefined,
  max:
    question.validation_rules?.data_type === 'number'
      ? question.validation_rules?.max_value
      : undefined,
  step:
    question.validation_rules?.data_type === 'number' &&
    question.validation_rules?.allow_decimal
      ? 'any'
      : '1',
  pattern: question.validation_rules?.pattern,
});

/**
 * Get common styling for disabled/readonly states
 */
export const getFieldStyles = (
  question: Question,
  baseStyle: React.CSSProperties = {}
) => ({
  cursor:
    question.attributes?.readOnly || question.attributes?.disabled
      ? 'not-allowed'
      : 'pointer',
  opacity:
    question.attributes?.readOnly || question.attributes?.disabled ? 0.6 : 1,
  ...question.attributes?.style,
  ...baseStyle,
});
